import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { map, type Observable, retry, shareReplay } from 'rxjs';

import { readProp } from '../../shared/utils/parse';
import { SettingsApi } from '../api/settings.api';
import type { ApiEnvelope } from './api-envelope';
import { apiPath, mapApiError, unwrapEnvelope } from './api-envelope';

/** Сбой на старте отнял бы карту на всю сессию — один повтор дешевле деградации. */
const RETRY_DELAY_MS = 1000;

@Injectable()
export class SettingsHttpApi extends SettingsApi {
  private readonly http = inject(HttpClient);

  /** Поток холодный: запрос уходит с первой подпиской, `shareReplay` раздаёт ответ остальным. */
  private readonly settings$ = this.http
    .get<ApiEnvelope<unknown> | null>(apiPath('/api/users/client/settings'))
    .pipe(
      unwrapEnvelope<unknown>(),
      map(readSettings),
      retry({ count: 1, delay: RETRY_DELAY_MS }),
      mapApiError(),
      // Без refCount: отписка последнего потребителя не должна отменить общий запрос.
      shareReplay({ bufferSize: 1, refCount: false }),
    );

  override load(): Observable<ReadonlyMap<string, string>> {
    return this.settings$;
  }
}

/** Map, а не объект: ключи приходят с бэкенда, и `settings['constructor']` не должен что-то находить. */
function readSettings(payload: unknown): ReadonlyMap<string, string> {
  const settings = new Map<string, string>();
  if (!Array.isArray(payload)) {
    return settings;
  }
  // В списке лежат и настройки, не связанные с картами: разбираем по `key`, а не по индексу.
  for (const entry of payload as unknown[]) {
    const key = readProp(entry, 'key');
    const value = readProp(entry, 'value');
    if (typeof key === 'string' && key.length > 0 && typeof value === 'string') {
      settings.set(key, value);
    }
  }
  return settings;
}
